import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as THREE from "three";
import "./loan-calculator.css";
import "bootstrap/dist/css/bootstrap.min.css";

const MIN_ROI = 0.9;
const MAX_ROI = 30;

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000);
  const [interestRate, setInterestRate] = useState(1);
  const [loanTenure, setLoanTenure] = useState(12);
  const canvasRef = useRef(null);

  // Flat monthly interest (e.g. 1%/day ≈ 30%/month):
  // 50,000 × 30% × 1 month = ₹15,000 interest
  const interestAmount = Math.round(
    loanAmount * (interestRate / 100) * loanTenure
  );
  const totalPayment = loanAmount + interestAmount;

  // 3D Pie Chart
  useEffect(() => {
    const canvas = canvasRef.current;
    const scene = new THREE.Scene();

    const width = 380;
    const height = 380;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });

    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    camera.position.z = 4;
    scene.add(camera);

    const light1 = new THREE.PointLight(0xffffff, 1.5);
    light1.position.set(5, 5, 5);
    scene.add(light1);

    const ambient = new THREE.AmbientLight(0xaaaaaa, 1.2);
    scene.add(ambient);

    const total = Math.max(loanAmount + interestAmount, 1);
    const principalAngle = (loanAmount / total) * Math.PI * 2;
    const interestAngle = (interestAmount / total) * Math.PI * 2;

    const principalMat = new THREE.MeshPhongMaterial({
      color: 0x0a1445,
      shininess: 120,
      specular: 0xffffff,
    });

    const interestMat = new THREE.MeshPhongMaterial({
      color: 0xfdd835,
      shininess: 120,
      specular: 0xffffff,
    });

    const height3D = 0.4;

    const principalGeo = new THREE.CylinderGeometry(
      1,
      1,
      height3D,
      64,
      1,
      false,
      0,
      principalAngle
    );

    const principalMesh = new THREE.Mesh(principalGeo, principalMat);
    principalMesh.rotation.x = Math.PI / 2;
    scene.add(principalMesh);

    const interestGeo = new THREE.CylinderGeometry(
      1,
      1,
      height3D,
      64,
      1,
      false,
      principalAngle,
      Math.max(interestAngle, 0.001)
    );

    const interestMesh = new THREE.Mesh(interestGeo, interestMat);
    interestMesh.rotation.x = Math.PI / 2;
    scene.add(interestMesh);

    const animate = () => {
      requestAnimationFrame(animate);
      principalMesh.rotation.z += 0.01;
      interestMesh.rotation.z += 0.01;
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      renderer.dispose();
      scene.clear();
    };
  }, [loanAmount, interestAmount]);

  return (
    <div className="loan-calculator-wrapper">
      <div className="loan-calculator-card shadow-lg rounded-4">
        <h2 className="loan-calculator-title mb-3">
          Smart <span className="highlight">Loan</span> Calculator
        </h2>

        <div className="row g-4">
          {/* LEFT */}
          <div className="col-md-6">
            <div className="emi-box p-3 rounded-3 text-center">
              <p>Your interest amount will be</p>
              <h3 className="emi-amount">
                ₹{interestAmount.toLocaleString()}
              </h3>
            </div>

            <div className="mt-4">
              <div className="d-flex justify-content-between mb-2">
                <div>
                  <span className="legend-dot yellow"></span> Interest Amount
                </div>
                <strong>₹{interestAmount.toLocaleString()}</strong>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <div>
                  <span className="legend-dot navy"></span> Principal Amount
                </div>
                <strong>₹{loanAmount.toLocaleString()}</strong>
              </div>

              <div className="d-flex justify-content-between">
                <div>
                  <span className="legend-dot mix"></span> Total Repayment
                </div>
                <strong>₹{totalPayment.toLocaleString()}</strong>
              </div>
            </div>

            <div className="text-center mt-4">
              <canvas ref={canvasRef}></canvas>
            </div>
          </div>

          {/* RIGHT */}
          <div className="col-md-6">
            {/* Loan Amount */}
            <div className="mb-3">
              <label className="fw-bold">Loan Amount</label>
              <input
                type="range"
                min="5000"
                max="50000"
                step="1000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(+e.target.value)}
                className="form-range"
              />
              <div className="d-flex justify-content-between">
                <small>Min ₹5K</small>
                <small>Max ₹50K</small>
              </div>
              <input
                type="text"
                className="form-control mt-2 text-end"
                value={`₹${loanAmount.toLocaleString()}`}
                readOnly
              />
            </div>

            {/* Interest — monthly rate (1%/day ≈ 30%/month); min 0.9% */}
            <div className="mb-3">
              <label className="fw-bold">Rate of Interest (monthly)</label>
              <input
                type="range"
                min={MIN_ROI}
                max={MAX_ROI}
                step="0.1"
                value={interestRate}
                onChange={(e) =>
                  setInterestRate(
                    Math.max(MIN_ROI, Math.min(MAX_ROI, +e.target.value))
                  )
                }
                className="form-range"
              />
              <div className="d-flex justify-content-between">
                <small>Min {MIN_ROI}%</small>
                <small>Max {MAX_ROI}%</small>
              </div>
              <input
                type="text"
                className="form-control mt-2 text-end"
                value={`${interestRate}%`}
                readOnly
              />
            </div>

            {/* Tenure */}
            <div className="mb-3">
              <label className="fw-bold">Loan Tenure</label>
              <input
                type="range"
                min="3"
                max="12"
                step="1"
                value={loanTenure}
                onChange={(e) => setLoanTenure(+e.target.value)}
                className="form-range"
              />
              <div className="d-flex justify-content-between">
                <small>Min 3 Months</small>
                <small>Max 12 Months</small>
              </div>
              <input
                type="text"
                className="form-control mt-2 text-end"
                value={`${loanTenure} Month(s)`}
                readOnly
              />
            </div>

            <div className="text-center mt-4">
              <Link to="/apply-now" className="btn btn-cal">
                Apply Now →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanCalculator;
